// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { Button, ImprTooltip } from '@imprivata-cloud/components';
import { Dropdown, Tag } from 'antd';
import type { MenuProps, TableColumnsType } from 'antd';
import { ChevronDown, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import type { useTranslation } from 'react-i18next';
import type { IdvConnectionSummary, IdvConnectionTemplate } from '../../../api/types';
import CopyButton from '../../../components/copyButton/copyButton';
import { getBadgeColor, getBadgeLabel } from './IdvConnectionsBadge';

export function buildColumns(
  t: ReturnType<typeof useTranslation>['t'],
  isBusy: boolean,
  onEdit: (conn: IdvConnectionSummary) => void,
  onDelete: (conn: IdvConnectionSummary) => void,
): TableColumnsType<IdvConnectionSummary> {
  return [
    {
      title: 'Name',
      dataIndex: 'displayName',
      key: 'displayName',
      width: 180,
      // The cell truncates with an ellipsis at 180px; a tooltip carrying the full name
      // makes a truncated value reachable (ICP defect: long names had no way to be read).
      render: (name: string) => (
        <ImprTooltip
          title={name}
          placement="topLeft"
          // `nowrap` keeps the name on one line; `maxWidth: none` lifts antd's 250px cap so the
          // black tooltip box grows to fit the whole name instead of the text overflowing it.
          styles={{ root: { maxWidth: 'none' }, body: { borderRadius: 8, whiteSpace: 'nowrap' } }}
        >
          <span className="idv-connection-name">{name}</span>
        </ImprTooltip>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'templateType',
      key: 'templateType',
      width: 160,
      render: (templateType: IdvConnectionTemplate) => (
        <Tag color={getBadgeColor(templateType)} className="idv-connection-badge">
          {getBadgeLabel(templateType, t)}
        </Tag>
      ),
    },
    {
      title: 'Redirect URIs',
      key: 'redirectUris',
      width: 240,
      render: (_: unknown, conn: IdvConnectionSummary) => {
        const uris = conn.redirectUris ?? [];
        return (
          <Dropdown
            trigger={['click']}
            overlayClassName="idv-redirect-uri-dropdown"
            disabled={uris.length === 0}
            placement="bottomRight"
            dropdownRender={() => (
              <div className="idv-uri-popup">
                {uris.map((uri) => (
                  <div key={uri} className="idv-uri-row">
                    <span className="idv-uri-text">{uri}</span>
                    <CopyButton textToCopy={uri} aria-label={`Copy ${uri}`} />
                  </div>
                ))}
              </div>
            )}
          >
            <button type="button" className="idv-redirect-uri-trigger">
              <span>{uris.length} {uris.length === 1 ? 'Redirect URI' : 'Redirect URIs'}</span>
              <ChevronDown size={16} />
            </button>
          </Dropdown>
        );
      },
    },
    {
      title: 'Client ID',
      key: 'clientId',
      width: 420,
      render: (_: unknown, conn: IdvConnectionSummary) => (
        <div className="idv-client-id-group">
          <span className="client-id-label">Client ID:</span>
          <span className="idv-connection-client-id">{conn.clientId}</span>
          <CopyButton
            textToCopy={conn.clientId}
            aria-label="Copy Client ID"
          />
        </div>
      ),
    },
    {
      key: 'actions',
      width: 100,
      render: (_: unknown, conn: IdvConnectionSummary) => {
        const items: MenuProps['items'] = [
          {
            key: 'edit',
            label: t('common.edit'),
            icon: <Pencil size={16} />,
            onClick: () => onEdit(conn),
          },
          {
            key: 'remove',
            label: t('common.remove'),
            icon: <Trash2 size={16} />,
            onClick: () => onDelete(conn),
          },
        ];
        return (
          <Dropdown menu={{ items }} trigger={['click']} overlayClassName="idv-integration-dropdown">
            <ImprTooltip title="Actions" placement="bottom" styles={{ body: { borderRadius: 8 } }}>
              <Button
                type="text"
                icon={<MoreVertical size={24} />}
                disabled={isBusy}
                className="idv-actions-menu-button"
                aria-label={`Actions for ${conn.displayName}`}
              />
            </ImprTooltip>
          </Dropdown>
        );
      },
    },
  ];
}
